/**
 * Theme Component Registry
 *
 * Maps theme names to their component overrides. When a theme specifies
 * a custom component, the registry returns the appropriate import.
 *
 * This is the core of the "veneer" system - themes can overlay custom
 * components on top of the base implementation.
 *
 * Usage:
 *   import { getThemedComponent } from '@/themes/veneer/componentRegistry'
 *   const LandingComponent = await getThemedComponent('LandingPage')
 */

// Base components - used when theme doesn't override
const baseComponents = {
  LandingPage: () => import('@/views/LandingPage.vue'),
  AppHeader: () => import('@/components/layout/AppHeader.vue'),
  FeaturedItems: () => import('@/components/FeaturedItems.vue'),
  AppFooter: () => import('@/components/layout/AppFooter.vue'),
}

// Theme-specific component overrides
// Each theme can override any base component with its own version
const themeOverrides = {
  // Westmoreland themes use base components (no overrides needed)
  westmoreland: {},
  westmoreland_v2: {},

  // Windermere themes use custom veneer components
  windermere: {
    LandingPage: () => import('@/themes/veneer/windermere/WindermereLanding.vue'),
    AppHeader: () => import('@/themes/veneer/windermere/WindermereHeader.vue'),
  },
  windermere_v2: {
    LandingPage: () => import('@/themes/veneer/windermere/WindermereLanding.vue'),
    AppHeader: () => import('@/themes/veneer/windermere/WindermereHeader.vue'),
  },
}

/**
 * Get the component import function for the current theme
 *
 * @param {string} componentName - The name of the component to load
 * @param {string} themeName - Optional theme name (defaults to window.VUE_APP_THEME)
 * @returns {Function} - Dynamic import function for the component
 */
export function getComponentImport(componentName, themeName = null) {
  const theme = themeName || window.VUE_APP_THEME || 'westmoreland'

  // Check for theme-specific override
  const overrides = themeOverrides[theme] || {}
  if (overrides[componentName]) {
    return overrides[componentName]
  }

  // Fall back to base component
  if (baseComponents[componentName]) {
    return baseComponents[componentName]
  }

  console.warn(`[ComponentRegistry] No component found for: ${componentName}`)
  return null
}

/**
 * Asynchronously load a themed component
 *
 * @param {string} componentName - The name of the component to load
 * @param {string} themeName - Optional theme name
 * @returns {Promise<Component>} - The loaded Vue component
 */
export async function getThemedComponent(componentName, themeName = null) {
  const importFn = getComponentImport(componentName, themeName)
  if (!importFn) {
    throw new Error(`Component not found: ${componentName}`)
  }

  const module = await importFn()
  return module.default || module
}

/**
 * Check if a theme has an override for a specific component
 *
 * @param {string} componentName - The component to check
 * @param {string} themeName - Optional theme name
 * @returns {boolean}
 */
export function hasThemeOverride(componentName, themeName = null) {
  const theme = themeName || window.VUE_APP_THEME || 'westmoreland'
  const overrides = themeOverrides[theme] || {}
  return !!overrides[componentName]
}

/**
 * Get list of all overridden components for a theme
 *
 * @param {string} themeName - Optional theme name
 * @returns {string[]} - Array of component names that are overridden
 */
export function getThemeOverrides(themeName = null) {
  const theme = themeName || window.VUE_APP_THEME || 'westmoreland'
  const overrides = themeOverrides[theme] || {}
  return Object.keys(overrides)
}

/**
 * Register a new theme override at runtime
 * Useful for dynamic theme loading or plugins
 *
 * @param {string} themeName - The theme to add override for
 * @param {string} componentName - The component to override
 * @param {Function} importFn - Dynamic import function for the component
 */
export function registerThemeOverride(themeName, componentName, importFn) {
  if (!themeOverrides[themeName]) {
    themeOverrides[themeName] = {}
  }
  themeOverrides[themeName][componentName] = importFn
}

export default {
  getComponentImport,
  getThemedComponent,
  hasThemeOverride,
  getThemeOverrides,
  registerThemeOverride,
}
